import { RefObject, useEffect, useRef, useState } from "react";

interface UsePullToStretchBannerOptions {
  /** Only wired up for a real photo banner — stretching a flat gradient
   *  reveals nothing, so callers should pass false for the generated case. */
  enabled: boolean;
  /** Max extra px the banner can grow by, before diminishing-returns damping. */
  maxPull?: number;
}

interface UsePullToStretchBannerResult {
  /** Spread onto the banner element. `undefined` at rest, so the existing
   *  responsive CSS height (mobile vs. desktop) stays in control until a
   *  drag actually starts. */
  style: React.CSSProperties | undefined;
  /** 0 (resting) -> 1 (fully pulled) — drive secondary feedback (e.g. scrim fade). */
  pullRatio: number;
}

const MAX_PULL_DEFAULT = 110;
const RELEASE_MS = 320;

/**
 * "Pull to stretch" hero banner, Airbnb/Instagram-style: dragging down past
 * the top of the page (touch) or scrolling up while already at the top
 * (desktop wheel) grows the banner's height live, revealing more of the
 * photo underneath (it's rendered with object-fit: cover, so a taller box
 * shows more of the image rather than distorting it). Releasing springs it
 * back to the normal height with a short elastic transition.
 *
 * Growth uses a sqrt damping curve so it feels elastic (fast at first, then
 * increasingly resistant) rather than 1:1 with the drag distance.
 */
export function usePullToStretchBanner(
  bannerRef: RefObject<HTMLElement>,
  { enabled, maxPull = MAX_PULL_DEFAULT }: UsePullToStretchBannerOptions
): UsePullToStretchBannerResult {
  const [extraPx, setExtraPx] = useState(0);
  const [isReleasing, setIsReleasing] = useState(false);
  const [atRest, setAtRest] = useState(true);

  const baseHeightRef = useRef(0);
  const touchStartYRef = useRef<number | null>(null);
  const rawWheelPullRef = useRef(0);
  const wheelIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const releaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const damp = (raw: number) => Math.min(maxPull, Math.sqrt(Math.max(0, raw)) * 6);
    const atTop = () => (document.scrollingElement?.scrollTop ?? 0) <= 0;

    const captureBaseHeight = () => {
      if (baseHeightRef.current === 0 && bannerRef.current) {
        baseHeightRef.current = bannerRef.current.getBoundingClientRect().height;
      }
    };

    const springBack = () => {
      // No base height captured means no real pull ever started (e.g. a
      // plain tap with zero drag) — bail so we don't flip into the inline
      // `height: 0px` render branch and visibly collapse the banner.
      if (baseHeightRef.current === 0) return;
      if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current);
      setIsReleasing(true);
      setExtraPx(0);
      releaseTimerRef.current = setTimeout(() => {
        setIsReleasing(false);
        setAtRest(true);
        baseHeightRef.current = 0;
      }, RELEASE_MS);
    };

    const onTouchStart = (e: TouchEvent) => {
      // Only arm the gesture if it actually starts on the banner — these
      // listeners are on `window` so every touch on the page reaches them,
      // including ones inside portal-rendered modals (ShareProfileModal,
      // CreateBusinessModal, etc.) that sit at scroll-top 0 just like the
      // page often does. Gating at the start (not on every move) means a
      // drag that continues past the banner's edge still tracks correctly.
      const startedOnBanner = !!bannerRef.current?.contains(e.target as Node);
      touchStartYRef.current = atTop() && startedOnBanner ? e.touches[0].clientY : null;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (touchStartYRef.current === null) return;
      if (!atTop()) {
        touchStartYRef.current = null;
        springBack();
        return;
      }
      const delta = e.touches[0].clientY - touchStartYRef.current;
      if (delta <= 0) return;
      // Actively stretching — stop the page's own scroll/bounce from
      // fighting with our custom one.
      e.preventDefault();
      captureBaseHeight();
      setAtRest(false);
      setIsReleasing(false);
      setExtraPx(damp(delta));
    };

    const onTouchEnd = () => {
      if (touchStartYRef.current === null) return;
      touchStartYRef.current = null;
      springBack();
    };

    const onWheel = (e: WheelEvent) => {
      // Same window-listener scoping as touch: only intercept wheel-up when
      // the cursor is actually over the banner, so scrolling inside a modal
      // (or anywhere else on the page) while the page itself sits at
      // scroll-top 0 doesn't get hijacked into stretching the banner.
      if (!atTop() || e.deltaY >= 0 || !bannerRef.current?.contains(e.target as Node)) {
        rawWheelPullRef.current = 0;
        return;
      }
      e.preventDefault();
      captureBaseHeight();
      rawWheelPullRef.current = Math.min(rawWheelPullRef.current - e.deltaY, 900);
      setAtRest(false);
      setIsReleasing(false);
      setExtraPx(damp(rawWheelPullRef.current));

      if (wheelIdleTimerRef.current) clearTimeout(wheelIdleTimerRef.current);
      // Wheel has no natural "release" event like touchend — treat a short
      // idle gap (no further pull-up ticks) as the release.
      wheelIdleTimerRef.current = setTimeout(() => {
        rawWheelPullRef.current = 0;
        springBack();
      }, 150);
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });
    window.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
      window.removeEventListener("wheel", onWheel);
      if (wheelIdleTimerRef.current) clearTimeout(wheelIdleTimerRef.current);
      if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current);
    };
  }, [enabled, maxPull, bannerRef]);

  // Disabling mid-drag (e.g. banner candidate changes) shouldn't leave a
  // stale stretched height behind.
  useEffect(() => {
    if (!enabled) {
      setExtraPx(0);
      setIsReleasing(false);
      setAtRest(true);
      baseHeightRef.current = 0;
    }
  }, [enabled]);

  const pullRatio = maxPull > 0 ? extraPx / maxPull : 0;

  if (atRest && !isReleasing) {
    return { style: undefined, pullRatio: 0 };
  }

  return {
    style: {
      height: `${baseHeightRef.current + extraPx}px`,
      transition: isReleasing ? `height ${RELEASE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)` : "none",
    },
    pullRatio,
  };
}
